import { Line } from '@react-three/drei';
import React, { useMemo } from 'react';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { useIntl } from 'react-intl';
import { WallLengthIndicatorProps } from '../../../@types/Components';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useMultiSelectToggleContext } from '../../../provider/MultiSelectToggleProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import {
  DEFAULT_INDICATOR_COLOR,
  DEFAULT_INDICATOR_FONT_SIZE,
  DEFAULT_INDICATOR_LINE_WIDTH,
  DEFAULT_WALL_INNER_OFFSET,
  DEFAULT_WALL_OUTER_OFFSET,
  INNER_WALL_OFFSET_ADJUSTED,
  WALL_TEXT_OFFSET
} from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

const WallLengthIndicator = ({
  startPoint,
  endPoint,
  outerStartPoint,
  outerEndPoint,
  wallSideALength,
  wallSideBLength,
  startReferencePoint,
  endReferencePoint,
  color = DEFAULT_INDICATOR_COLOR,
  lineWidth = DEFAULT_INDICATOR_LINE_WIDTH,
  shiftIndicator = false,
  showLine = true,
}: WallLengthIndicatorProps): React.JSX.Element => {
  const { showWallOuterLength, showWallInnerLength } = useMultiSelectToggleContext();
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();
  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'meter'), [locale]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'foot'), [locale]);

  const toVector3 = (point: number[] | THREE.Vector3): THREE.Vector3 => {
    if ((point as THREE.Vector3).isVector3) {
      return point as THREE.Vector3;
    }
    return new THREE.Vector3((point as number[])[0], (point as number[])[1], 0);
  };

  const pointA = useMemo(() => toVector3(startPoint), [startPoint]);
  const pointB = useMemo(() => toVector3(endPoint), [endPoint]);
  const vectorAB = useMemo(() => new THREE.Vector3().subVectors(pointB, pointA), [pointA, pointB]);
  const perpendicular = new THREE.Vector3(vectorAB.y, -vectorAB.x).normalize();

  const pointAReference = useMemo(() => (startReferencePoint ? toVector3(startReferencePoint) : undefined), [startReferencePoint]);
  const pointBReference = useMemo(() => (endReferencePoint ? toVector3(endReferencePoint) : undefined), [endReferencePoint]);
  const vectorABReference = useMemo(
    () => (pointAReference && pointBReference ? new THREE.Vector3().subVectors(pointBReference, pointAReference) : undefined),
    [pointAReference, pointBReference],
  );
  const perpendicularReference = vectorABReference ? new THREE.Vector3(vectorABReference.y, -vectorABReference.x).normalize() : undefined;

  // Use the shiftIndicator prop to determine the inner offset
  const adjustedInnerOffset = shiftIndicator ? INNER_WALL_OFFSET_ADJUSTED : DEFAULT_WALL_INNER_OFFSET;
  const innerOffset = useMemo(() => perpendicular.clone().multiplyScalar(adjustedInnerOffset), [adjustedInnerOffset, perpendicular]);
  const outerOffset = useMemo(
    () => (perpendicularReference ? perpendicularReference.clone().multiplyScalar(-DEFAULT_WALL_OUTER_OFFSET) : perpendicular.clone().multiplyScalar(-DEFAULT_WALL_OUTER_OFFSET)),
    [perpendicular, perpendicularReference],
  );

  const pointInnerC = useMemo(() => pointA.clone().add(innerOffset), [pointA, innerOffset]);
  const pointInnerD = useMemo(() => pointB.clone().add(innerOffset), [pointB, innerOffset]);

  const pointC = outerStartPoint ? toVector3(outerStartPoint) : null;
  const pointD = outerEndPoint ? toVector3(outerEndPoint) : null;
  const pointOuterC = pointC ? pointC.clone().add(outerOffset) : null;
  const pointOuterD = pointD ? pointD.clone().add(outerOffset) : null;

  const midPointOuter = useMemo(() => {
    // Check if both pointOuterC and pointOuterD are available
    if (pointOuterC && pointOuterD) {
      const textPerpendOuter = perpendicular.clone().multiplyScalar(-WALL_TEXT_OFFSET);
      const aboveOuterC = pointOuterC.clone().add(textPerpendOuter);
      const aboveOuterD = pointOuterD.clone().add(textPerpendOuter);
      return new THREE.Vector3((aboveOuterC.x + aboveOuterD.x) / 2, (aboveOuterC.y + aboveOuterD.y) / 2, 1);
    }
    return null;
  }, [pointOuterC, pointOuterD, perpendicular]);

  const midPointInner = useMemo(() => {
    const textPerpendInner = perpendicular.clone().multiplyScalar(WALL_TEXT_OFFSET);
    const aboveInnerC = pointInnerC.clone().add(textPerpendInner);
    const aboveInnerD = pointInnerD.clone().add(textPerpendInner);
    return new THREE.Vector3(
      (aboveInnerC.x + aboveInnerD.x) / 2,
      (aboveInnerC.y + aboveInnerD.y) / 2,
      1, // Z position
    );
  }, [pointInnerC, pointInnerD, perpendicular]);

  const sideAlengthText = useMemo(
    () =>
      wallSideALength
        ? unitType === EUnitType.METRIC
          ? localizeNumber(wallSideALength.toFixed(2), locale) + mLocalizedLabel
          : localizeNumber(convertTo(wallSideALength, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) +
            ftLocalizedLabel
        : '',
    [wallSideALength, unitType, locale],
  );
  const sideBlengthText = useMemo(
    () =>
      wallSideBLength
        ? unitType === EUnitType.METRIC
          ? localizeNumber(wallSideBLength.toFixed(2), locale) + mLocalizedLabel
          : localizeNumber(convertTo(wallSideBLength, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) +
            ftLocalizedLabel
        : '',
    [wallSideBLength, unitType, locale],
  );

  return (
    <group name={'group-wallLengthIndicator'} renderOrder={RENDER_ORDERS.lengthIndicator}>
      {/* Inner wall length indicator */}
      {showWallInnerLength && (
        <>
          {showLine && (
            <>
              <Line name={'innerWallLengthIndicator-line'} points={[pointInnerC, pointInnerD]} color={color} lineWidth={lineWidth} transparent={true} />
              <Line name={'innerWallLengthIndicator-line'} points={[pointA, pointInnerC]} color={color} lineWidth={lineWidth} transparent={true} />
              <Line name={'innerWallLengthIndicator-line'} points={[pointB, pointInnerD]} color={color} lineWidth={lineWidth} transparent={true} />
            </>
          )}
          <RoundedBoxWithText position={midPointInner} fontSize={DEFAULT_INDICATOR_FONT_SIZE} textColor="black" text={sideAlengthText} />
        </>
      )}
      {/* Conditional rendering for outer wall length indicator */}
      {wallSideBLength && pointOuterC && pointOuterD && showWallOuterLength && (
        <>
          <Line name={'outerWallLengthIndicator-line'} points={[pointOuterC, pointOuterD]} color={color} lineWidth={lineWidth} transparent={true} />
          {pointC !== null && <Line name={'outerWallLengthIndicator-line'} points={[pointC, pointOuterC]} color={color} lineWidth={lineWidth} transparent={true} />}
          {pointD !== null && <Line name={'outerWallLengthIndicator-line'} points={[pointD, pointOuterD]} color={color} lineWidth={lineWidth} transparent={true} />}
          <RoundedBoxWithText position={midPointOuter!} fontSize={DEFAULT_INDICATOR_FONT_SIZE} textColor="black" text={sideBlengthText} />
        </>
      )}
    </group>
  );
};

export default WallLengthIndicator;
